import type {
  ExpressionNodeFe,
  ExpressionPayloadFe,
} from "@intelligems/ig-types";

export type operator = "or" | "and";

export const expressionListToInfix = <TQuery extends { type: string }>(
  expressionsList: ExpressionPayloadFe<TQuery>[],
  ingroupOperator: operator = "or",
  outgroupOperator: operator = "and"
): ExpressionNodeFe<TQuery>[] => {
  const infix: ExpressionNodeFe<TQuery>[] = [];
  for (let i = 0; i < expressionsList.length; i++) {
    const queries = expressionsList[i].queries;
    if (!queries.length) {
      continue;
    }

    infix.push({ operator: "start_paren" });
    for (let j = 0; j < queries.length; j++) {
      const query = queries[j];
      infix.push({ query });
      if (j !== queries.length - 1) {
        infix.push({ operator: ingroupOperator });
      }
    }
    infix.push({ operator: "end_paren" });
    if (i !== expressionsList.length - 1) {
      infix.push({ operator: outgroupOperator });
    }
  }
  return infix;
};

export const infixToExpressionList = <TQuery extends { type: string }>(
  infix: ExpressionNodeFe<TQuery>[],
  outgroupOperator: operator = "and"
): ExpressionPayloadFe<TQuery>[] => {
  const expressionsList: ExpressionPayloadFe<TQuery>[] = [];

  let payload: ExpressionPayloadFe<TQuery> | null = null;
  for (let i = 0; i < infix.length; i++) {
    const curr = infix[i];
    if (curr.query) {
      if (!payload) {
        payload = {
          type: curr.query.type,
          queries: [curr.query],
        };
      } else {
        payload.queries.push(curr.query);
      }
    } else if (curr.operator === outgroupOperator && payload) {
      expressionsList.push(payload);
      payload = null;
    }
  }
  if (payload) {
    expressionsList.push(payload);
  }
  return expressionsList;
};

export const postfixToInfix = <TQuery>(
  postfix: ExpressionNodeFe<TQuery>[]
): ExpressionNodeFe<TQuery>[] => {
  const stack: ExpressionNodeFe<TQuery>[][] = [[]];
  for (let i = 0; i < postfix.length; i++) {
    const curr = postfix[i];
    if (postfix[i].query) {
      stack.push([curr]);
    } else {
      const op1 = stack.pop();
      const op2 = stack.pop();
      if (op1 !== undefined && op2 !== undefined) {
        stack.push([
          { operator: "start_paren" },
          ...op2,
          postfix[i],
          ...op1,
          { operator: "end_paren" },
        ]);
      }
    }
  }
  return stack[stack.length - 1] ?? [];
};

export const infixToPostfix = <TQuery>(
  infix: ExpressionNodeFe<TQuery>[]
): ExpressionNodeFe<TQuery>[] => {
  const postfix: ExpressionNodeFe<TQuery>[] = [];
  const stack: ExpressionNodeFe<TQuery>[] = [];
  for (const token of infix) {
    if (token.query) {
      postfix.push(token);
    } else if (token.operator === "start_paren") {
      stack.push(token);
    } else if (token.operator === "end_paren") {
      while (
        stack.length &&
        stack[stack.length - 1].operator !== "start_paren"
      ) {
        const item = stack.pop();
        if (item) {
          postfix.push(item);
        }
      }
      stack.pop();
    } else {
      while (
        stack.length &&
        (stack[stack.length - 1].operator === "and" ||
          stack[stack.length - 1].operator === "or")
      ) {
        const item = stack.pop();
        if (item) {
          postfix.push(item);
        }
      }
      stack.push(token);
    }
  }
  while (stack.length) {
    const item = stack.pop();
    if (item) {
      postfix.push(item);
    }
  }
  return postfix;
};

export const postfixToExpressionList = <TQuery extends { type: string }>(
  postfix: ExpressionNodeFe<TQuery>[],
  mode:
    | "audience"
    | "pageTargeting"
    | "experienceProductTargeting"
    | "cartTargeting"
    | "upsellProductTargeting" = "audience"
): ExpressionPayloadFe<TQuery>[] => {
  let outgroupOperator: operator = "and";
  if (
    [
      "pageTargeting",
      "experienceProductTargeting",
      "cartTargeting",
      "upsellProductTargeting",
    ].includes(mode)
  ) {
    outgroupOperator = "or";
  }

  const infix: ExpressionNodeFe<TQuery>[] = postfixToInfix(postfix);
  const expressionsList: ExpressionPayloadFe<TQuery>[] = infixToExpressionList(
    infix,
    outgroupOperator
  );
  return expressionsList;
};

export const expressionListToPostFix = <TQuery extends { type: string }>(
  expressionsList: ExpressionPayloadFe<TQuery>[],
  mode:
    | "audience"
    | "pageTargeting"
    | "experienceProductTargeting"
    | "cartTargeting"
    | "upsellProductTargeting" = "audience"
): ExpressionNodeFe<TQuery>[] => {
  let { ingroupOperator, outgroupOperator } = {
    ingroupOperator: "or" as operator,
    outgroupOperator: "and" as operator,
  };
  if (
    [
      "pageTargeting",
      "experienceProductTargeting",
      "cartTargeting",
      "upsellProductTargeting",
    ].includes(mode)
  ) {
    ingroupOperator = "and";
    outgroupOperator = "or";
  }
  const infix: ExpressionNodeFe<TQuery>[] = expressionListToInfix(
    expressionsList,
    ingroupOperator,
    outgroupOperator
  );
  const postfix: ExpressionNodeFe<TQuery>[] = infixToPostfix(infix);
  return postfix;
};
